import { HttpService } from "data/http-service";
import {
  DownloadLinkPayload,
  FavoriteActivity,
} from "domain/entities/activities.entities";
import { Snackbar } from "ui/pages/snackbar";
import { translate } from "ui/i18n";
import { AxiosError } from "axios";
import { ActivitiesGateway } from "domain/activities/activities.gateway";
import {
  favoriteActivityDtoSchema,
  favoriteActivitiesDtoSchema,
} from "data/activities/activities.dto.schemas";

export class ActivitiesHttpGateway implements ActivitiesGateway {
  async setTechniqueFavorite(techniqueId: number): Promise<{
    technique: FavoriteActivity | null;
    success: boolean;
  }> {
    return HttpService.post(`user-favorites`, { techniqueId })
      .then((response) => {
        if (response.status === 201) {
          const parsedActivity = favoriteActivityDtoSchema.safeParse(
            response.data
          );
          if (parsedActivity.success) {
            return {
              technique: parsedActivity.data,
              success: true,
            };
          } else return { technique: null, success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Activities.SetFavoriteError"),
          });
          return {
            technique: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Activities.SetFavoriteError"),
        });
        return { technique: null as FavoriteActivity, success: false };
      });
  }

  async setSeanceFavorite(seanceId: number): Promise<{
    seance: FavoriteActivity | null;
    success: boolean;
  }> {
    return HttpService.post(`user-favorites`, { seanceId })
      .then((response) => {
        if (response.status === 201) {
          const parsedActivity = favoriteActivityDtoSchema.safeParse(
            response.data
          );
          if (parsedActivity.success) {
            return {
              seance: parsedActivity.data,
              success: true,
            };
          } else return { seance: null, success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Activities.SetFavoriteError"),
          });
          return {
            seance: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Activities.SetFavoriteError"),
        });
        return { seance: null as FavoriteActivity, success: false };
      });
  }

  async setTutorialFavorite(tutorialId: number): Promise<{
    tutorial: FavoriteActivity | null;
    success: boolean;
  }> {
    return HttpService.post(`user-favorites`, { tutorialId })
      .then((response) => {
        if (response.status === 201) {
          const parsedActivity = favoriteActivityDtoSchema.safeParse(
            response.data
          );
          if (parsedActivity.success) {
            return {
              tutorial: parsedActivity.data,
              success: true,
            };
          } else return { tutorial: null, success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Activities.SetFavoriteError"),
          });
          return {
            tutorial: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Activities.SetFavoriteError"),
        });
        return { tutorial: null as FavoriteActivity, success: false };
      });
  }

  async setVisualSupportFavorite(supportId: number): Promise<{
    support: FavoriteActivity | null;
    success: boolean;
  }> {
    return HttpService.post(`user-favorites`, { visuelId: supportId })
      .then((response) => {
        if (response.status === 201) {
          const parsedActivity = favoriteActivityDtoSchema.safeParse(
            response.data
          );
          if (parsedActivity.success) {
            return {
              support: parsedActivity.data,
              success: true,
            };
          } else return { support: null, success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Activities.SetFavoriteError"),
          });
          return {
            support: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Activities.SetFavoriteError"),
        });
        return { support: null as FavoriteActivity, success: false };
      });
  }

  async getFavoriteActivities(): Promise<{
    activities: FavoriteActivity[];
    success: boolean;
  }> {
    return HttpService.get(`user-favorites`)
      .then((response) => {
        if (response.status === 200) {
          const parsedActivities = favoriteActivitiesDtoSchema.safeParse(
            response.data
          );
          if (parsedActivities.success) {
            return {
              activities: parsedActivities.data,
              success: true,
            };
          } else return { activities: [], success: false };
        } else {
          Snackbar.showError({
            title: translate("ns1:Activities.GetFavoriteError"),
          });
          return {
            activities: [],
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Activities.GetFavoriteError"),
        });
        return { activities: [] as FavoriteActivity[], success: false };
      });
  }

  async removeFavoriteActivity(id: number): Promise<{
    id: number | null;
    success: boolean;
  }> {
    return HttpService.delete(`user-favorites/${id}`)
      .then((response) => {
        if (response.status === 200) {
          return {
            id,
            success: true,
          };
        } else {
          Snackbar.showError({
            title: translate("ns1:Activities.RemoveFavoriteError"),
          });
          return {
            id: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Activities.RemoveFavoriteError"),
        });
        return { id: null as number, success: false };
      });
  }

  async getPremiumActivitiesCount(): Promise<{
    count: number;
    success: boolean;
  }> {
    const params = { $limit: 0, isInniz: true };
    const techniquesResponse = HttpService.get("techniques", { params });
    const seancesResponse = HttpService.get("seances", { params });
    const visuelsResponse = HttpService.get("visuels", { params });
    const tutorialsResponse = HttpService.get("tutorials", { params });
    return Promise.all([
      techniquesResponse,
      seancesResponse,
      visuelsResponse,
      tutorialsResponse,
    ]).then((responses) => {
      if (responses.every((response) => response.status === 200)) {
        const totalCount = responses.reduce(
          (acc, response) => acc + response.data.total,
          0
        );
        return { count: totalCount, success: true };
      } else {
        Snackbar.showError({
          title: translate("ns1:Activities.GetActivitiesCountError"),
        });
        return { count: 0, success: false };
      }
    });
  }

  async getDownloadLink(body: DownloadLinkPayload): Promise<{
    url: string | null;
    success: boolean;
  }> {
    return HttpService.post("storage/download", body)
      .then((response) => {
        if (response.status === 201 && response.data.url) {
          return {
            url: response.data.url,
            success: true,
          };
        } else {
          Snackbar.showError({
            title: translate("ns1:Activities.GetDownloadLinkError"),
          });
          return {
            url: null,
            success: false,
          };
        }
      })
      .catch((error: AxiosError) => {
        Snackbar.showError({
          title: translate("ns1:Activities.GetDownloadLinkError"),
        });
        return { url: null as string | null, success: false };
      });
  }
}
